import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type MailboxSchema, mailboxSchema } from "@msuite/utils/schemas";
import type { Enums } from "@msuite/utils/types";
import { FormProvider, useForm } from "react-hook-form";
import { MailboxForm } from "@/forms/mailbox-form";
import { client } from "@/hono";

/** Props Interface */
interface AddMailboxModalProps extends ModalProps {
	purpose: Enums<"mailboxPurpose">;
	onComplete?: () => unknown;
}

export function AddMailboxModal({ isOpen, onClose, purpose, onComplete }: AddMailboxModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm<MailboxSchema & { preset?: string }>({
		resolver: resolver(mailboxSchema),
		defaultValues: {
			purpose,
			label: "",
			emailAddress: "",
			folder: "INBOX",
			imapHost: "",
			imapPort: 993,
			imapSecurity: "tls",
			imapUsername: "",
			password: "",
			pollingIntervalMinutes: 5,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: MailboxSchema) {
		// The connect check runs on the server before anything is stored; a refusal comes back
		// as a localised toast through `responseHandler` and the modal stays open.
		const response = await client.mailboxes.index.$post({ json });
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("mailboxes.add_mailbox")}
				isLoading={methods.formState.isLoading}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				data-cy="add-mailbox-modal"
			>
				<Modal.Body>
					<MailboxForm isEdit={false} />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
